"use strict";
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.WebExtensionController = exports.canRequestApproval = void 0;
const bowser_1 = require("bowser");
const rxjs_1 = require("rxjs");
const errors_1 = require("./errors");
const webapp_contentScripts_messages_1 = require("./internal/webapp-contentScripts-messages");
const models_1 = require("./models");
function canRequestApproval(status) {
    return (status.type === models_1.WebExtensionStatusType.NO_AVAILABLE &&
        status.isApproved === false);
}
exports.canRequestApproval = canRequestApproval;
class WebExtensionController {
    constructor(hostWindow) {
        this.hostWindow = hostWindow;
        /**
         * Refetch the clientsStates
         *
         * You don't need call this method in most cases.
         * Normally, when the clientStates is changed, states() get the new clientStates.
         *
         * @example
         * client.states()
         *       .subscribe(states => {
         *         // 2. will get new clientStates
         *         console.log('Got new states', Date.now())
         *       })
         *
         * function callback() {
         *   // 1. refetch client states
         *   client.refetchStates()
         * }
         */
        this.refetchStates = () => {
            const msg = {
                type: webapp_contentScripts_messages_1.FromWebToContentScriptMessage.REFETCH_STATES,
            };
            this.hostWindow.postMessage(msg, '*');
        };
        /**
         * Request approval connection to the Extension. (Connect)
         */
        this.requestApproval = () => {
            const currentStatus = this._status.getValue();
            if (canRequestApproval(currentStatus)) {
                const msg = {
                    type: webapp_contentScripts_messages_1.FromWebToContentScriptMessage.REQUEST_APPROVAL,
                };
                this.hostWindow.postMessage(msg, '*');
            }
            else {
                console.warn(`requestApproval() is ignored. do not call at this status is "${JSON.stringify(currentStatus)}"`);
            }
        };
        this.status = () => {
            return this._status.asObservable();
        };
        this.getLastStatus = () => {
            return this._status.getValue();
        };
        /**
         * Execute transaction
         *
         * @example
         * client.post({ terraAddress, network, tx: CreateTxOptions })
         *       .subscribe({
         *          next: (result: WebExtensionTxProgress | WebExtensionTxSucceed) => {
         *            switch (result.status) {
         *              case WebExtensionTxStatus.PROGRESS:
         *                console.log('in progress', result.payload)
         *                break;
         *              case WebExtensionTxStatus.SUCCEED:
         *                console.log('succeed', result.payload)
         *                break;
         *            }
         *          },
         *          error: (error) => {
         *            if (error instanceof WebExtensionUserDenied) {
         *              console.log('user denied')
         *            } else if (error instanceof WebExtensionCreateTxFailed) {
         *              console.log('create tx failed', error.message)
         *            } else if (error instanceof WebExtensionTxFailed) {
         *              console.log('tx failed', error.txhash, error.message, error.raw_message)
         *            } else {
         *              console.log('unspecified error', 'message' in error ? error.message : String(error))
         *            }
         *          }
         *       })
         *
         * @description The stream will be
         * TxProgress -> [...TxProgress] -> TxSucceed
         *
         * - Tx is Succeed : TxProgress -> [...TxProgress] -> TxSucceed
         */
        this.post = ({ terraAddress, network, tx }) => {
            return new rxjs_1.Observable((subscriber) => {
                subscriber.next({
                    status: models_1.WebExtensionTxStatus.PROGRESS,
                });
                const id = Date.now();
                const msg = {
                    type: webapp_contentScripts_messages_1.FromWebToContentScriptMessage.EXECUTE_TX,
                    id,
                    terraAddress,
                    network,
                    payload: models_1.serializeTx(tx),
                };
                this.hostWindow.postMessage(msg, '*');
                const callback = (event) => {
                    if (!webapp_contentScripts_messages_1.isWebExtensionMessage(event.data) ||
                        event.data.type !== webapp_contentScripts_messages_1.FromContentScriptToWebMessage.TX_RESPONSE ||
                        event.data.id !== id) {
                        return;
                    }
                    if (event.data.payload.status === models_1.WebExtensionTxStatus.PROGRESS) {
                        subscriber.next(event.data.payload);
                    }
                    else if (event.data.payload.status === models_1.WebExtensionTxStatus.DENIED ||
                        event.data.payload.status === models_1.WebExtensionTxStatus.FAIL ||
                        event.data.payload.status === models_1.WebExtensionTxStatus.SUCCEED) {
                        switch (event.data.payload.status) {
                            case models_1.WebExtensionTxStatus.DENIED:
                                subscriber.error(new errors_1.WebExtensionUserDenied());
                                break;
                            case models_1.WebExtensionTxStatus.FAIL:
                                subscriber.error(event.data.payload.error instanceof Error
                                    ? event.data.payload.error
                                    : errors_1.createTxErrorFromJson(event.data.payload.error));
                                break;
                            case models_1.WebExtensionTxStatus.SUCCEED:
                                subscriber.next(event.data.payload);
                                subscriber.complete();
                                break;
                        }
                        this.hostWindow.removeEventListener('message', callback);
                    }
                };
                this.hostWindow.addEventListener('message', callback);
                return () => {
                    this.hostWindow.removeEventListener('message', callback);
                };
            });
        };
        /**
         * @example
         * client.states()
         *       .subscribe(states => {
         *         if (!states) {
         *           console.log('client is still not ready')
         *         } else {
         *           console.log('current network is', states.network)
         *           console.log('current wallets are', states.wallets)
         *         }
         *       })
         */
        this.states = () => {
            return this._states.asObservable();
        };
        this.getLastStates = () => {
            return this._states.getValue();
        };
        /**
         * Destroy this client
         *
         * - Unsubscribe all RxJs Subjects (every Observables are stoped)
         */
        this.destroy = () => {
            this.hostWindow.removeEventListener('message', this.onMessage);
        };
        this.onMessage = (event) => {
            if (!webapp_contentScripts_messages_1.isWebExtensionMessage(event.data)) {
                return;
            }
            switch (event.data.type) {
                case webapp_contentScripts_messages_1.FromContentScriptToWebMessage.STATES_UPDATED:
                    const currentStatus = this._status.getValue();
                    const _a = event.data.payload, { isApproved } = _a, nextStates = __rest(_a, ["isApproved"]);
                    if (isApproved) {
                        if (currentStatus.type !== models_1.WebExtensionStatusType.READY) {
                            this._status.next({
                                type: models_1.WebExtensionStatusType.READY,
                            });
                        }
                    }
                    else {
                        if (currentStatus.type !== models_1.WebExtensionStatusType.NO_AVAILABLE) {
                            this._status.next({
                                type: models_1.WebExtensionStatusType.NO_AVAILABLE,
                                isSupportBrowser: true,
                                isInstalled: true,
                                isApproved: false,
                            });
                        }
                    }
                    this._states.next(nextStates);
                    break;
            }
        };
        this._status = new rxjs_1.BehaviorSubject({
            type: models_1.WebExtensionStatusType.INITIALIZING,
        });
        this._states = new rxjs_1.BehaviorSubject(null);
        const meta = document.querySelector('head > meta[name="terra-webextension"]');
        if (!meta) {
            return;
        }
        const browser = bowser_1.getParser(navigator.userAgent);
        const isSupportBrowser = browser.satisfies({
            desktop: {
                chrome: '>70',
                edge: '>80',
                firefox: '>80',
                safari: '>=14',
            },
        });
        if (!isSupportBrowser) {
            this._status.next({
                type: models_1.WebExtensionStatusType.NO_AVAILABLE,
                isSupportBrowser: false,
            });
            return;
        }
        hostWindow.addEventListener('message', this.onMessage);
        this.refetchStates();
        // TODO improve first status check
        setTimeout(() => {
            if (this._status.getValue().type === models_1.WebExtensionStatusType.INITIALIZING) {
                const name = browser.getBrowserName(true);
                let installLink;
                switch (name) {
                    case 'chrome':
                    case 'microsoft edge':
                        installLink = 'https://google.com/chrome';
                        break;
                    case 'firefox':
                        installLink = 'https://google.com/firefox';
                        break;
                    case 'safari':
                        installLink = 'https://google.com/safari';
                        break;
                    default:
                        installLink = 'https://google.com/chrome';
                        break;
                }
                this._status.next({
                    type: models_1.WebExtensionStatusType.NO_AVAILABLE,
                    isSupportBrowser: true,
                    isInstalled: false,
                    installLink,
                });
            }
        }, 2000);
        // TODO refetchStates() on visibilitychange event
    }
}
exports.WebExtensionController = WebExtensionController;
//# sourceMappingURL=controller.js.map